// Libraries
import 'moment/locale/en-gb';
import 'moment/locale/pt';
// Assets
import 'assets/vendors/style';

import { useEffect, useRef, useState } from 'react';
import MomentUtils from '@date-io/moment';
import { Button } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
// Moment
import moment from 'moment';
import CookieConsent from 'react-cookie-consent';
import { IntlProvider } from 'react-intl';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import CookieBanner from 'components/CookieBanner';
import CustomizeCookiesDialog from 'components/CustomizeCookiesDialog';
// Components
import Error404 from 'components/Error404';
import MuiOverridesProvider from 'components/MuiOverridesProvider';
import IntlMessages from 'components/util/IntlMessages';

// Constants
import {
    BASENAME_URL,
    BASENAME_URL_ADFS_TOKEN_EXPIRY,
    BASENAME_URL_APP,
    BASENAME_URL_AUTH_ADFS,
    BASENAME_URL_LOGIN,
} from 'constants/settings';

import useCookies from 'hooks/cookies';
// Hooks
import useRedirectToken from 'hooks/redirectToken';

import AppLocale from 'lngProvider/index';

// Pages
import Login from 'pages/auth/Login';
import ResetPassword from 'pages/auth/ResetPassword';
import TokenExpiryADFS from 'pages/auth/tokenExpiryADFS';
import CacheBuster from 'pages/CacheBuster';

// Stores
import { useUserStore } from 'stores/user';

import LogoutADFS from './auth/LogoutADFS';
import Restricted from './Restricted';

// Dev: detect undefined component (causes "Element type is invalid... got: undefined")
const _imports = {
    StyledEngineProvider,
    LocalizationProvider,
    IntlProvider,
    CssBaseline,
    Login,
    ResetPassword,
    CacheBuster,
    TokenExpiryADFS,
    Restricted,
    Error404,
    LogoutADFS,
    CookieBanner,
    IntlMessages,
    CustomizeCookiesDialog,
    MuiOverridesProvider,
    Button,
};
Object.keys(_imports).forEach((key) => {
    if (_imports[key] === undefined) {
        console.error(`App.js: undefined import "${key}" – check default vs named export.`);
    }
});

const App = () => {
    const cookieConsentRef = useRef();

    const { setCookie, PLATFORM_CUSTOMIZATION_COOKIE, PERSONALIZED_ADVERTISING_COOKIE } = useCookies();

    const { locale } = useUserStore((state) => state.user);

    //Cookies handler to accept consent and other privacy cookies with default values (true)
    const cookiesHandler = () => {
        setCookie(PLATFORM_CUSTOMIZATION_COOKIE, true, 355);
        setCookie(PERSONALIZED_ADVERTISING_COOKIE, true, 365);
        cookieConsentRef.current.accept();
    };

    const location = useLocation();
    let searchParams = new URLSearchParams(location.search);
    const { isValidRedirectToken } = useRedirectToken();
    isValidRedirectToken(searchParams);

    const [isCustomizeDialogOpen, setIsCustomizeDialogOpen] = useState(false);

    //set localePickers locale (locale fro DatePickers, TimePickers etc)
    const currentAppLocale = AppLocale[locale.locale];

    //set localePickers locale (locale fro DatePickers, TimePickers etc)
    moment.locale(currentAppLocale.locale);

    // Gets company thme or defaults to defaultTheme
    // const appliedTheme = useRef(createTheme(
    //     baseTheme ?? defaultTheme
    // ));

    useEffect(() => {
        window.__MUI_USE_NEXT_TYPOGRAPHY_VARIANTS__ = true;
    }, []); //eslint-disable-line

    // * THEME SELECTION * \\
    // Can't be inside a useEffect because it only triggers after the component finishes rendering, and we need the theme when it's rendering
    // Material UI theme set
    // appliedTheme.current = createTheme(baseTheme ?? defaultTheme);
    // appliedTheme.current = {
    //     ...appliedTheme.current,
    //     ...muiOverrides
    // };

    // // Set CSS Theme colors
    // let bodyStyles = document.body.style;
    // bodyStyles.setProperty('--primary-color', appliedTheme.current.palette.primary.main);
    // bodyStyles.setProperty('--primary-color-light', appliedTheme.current.palette.primary.light);
    // bodyStyles.setProperty('--primary-color-dark', appliedTheme.current.palette.primary.dark);
    // bodyStyles.setProperty('--primary-color-bleached', appliedTheme.current.palette.primary.bleached);
    // bodyStyles.setProperty('--secondary-color', appliedTheme.current.palette.secondary.main);
    // bodyStyles.setProperty('--secondary-color-light', appliedTheme.current.palette.secondary.light);
    // bodyStyles.setProperty('--secondary-color-dark', appliedTheme.current.palette.secondary.dark);
    // if (!!appliedTheme.current.palette.tertiary) {
    //     bodyStyles.setProperty('--tertiary-color', appliedTheme.current.palette.tertiary.main);
    //     bodyStyles.setProperty('--tertiary-color-light', appliedTheme.current.palette.tertiary.bleached);
    //     bodyStyles.setProperty('--tertiary-color-dark', appliedTheme.current.palette.tertiary.main);
    // }
    // bodyStyles.setProperty('--text-color', appliedTheme.current.palette.text.primary);
    // bodyStyles.setProperty('--text-color-secondary', appliedTheme.current.palette.text.secondary);
    // bodyStyles.setProperty('--divider-color', appliedTheme.current.palette.divider.main);
    // bodyStyles.setProperty('--disabled-color', appliedTheme.current.palette.action.disabled);
    // bodyStyles.setProperty('--disabled-bg-color', appliedTheme.current.palette.action.disabledBackground);

    return (
        <CacheBuster location={location}>
            <StyledEngineProvider injectFirst>
                {/* <ThemeProvider theme={appliedTheme.current}> */}
                {/* <EfzThemeProvider theme={appliedTheme.current}> */}
                <LocalizationProvider dateAdapter={MomentUtils} locale={locale.localePickers}>
                    <IntlProvider key={locale.locale} locale={currentAppLocale.locale} messages={currentAppLocale.messages}>
                        <CssBaseline />
                        <MuiOverridesProvider />
                        <div className="app-main">
                            <Routes>
                                <Route path={`${BASENAME_URL_APP}/*`} element={<Restricted />} />
                                {/* route auth efz */}
                                <Route path={`${BASENAME_URL}`} element={<Login />} />
                                <Route path={BASENAME_URL_LOGIN} element={<Login />} />
                                <Route path={`${BASENAME_URL}reset-password`} element={<ResetPassword />} />
                                {/* route auth adfs */}
                                <Route path={BASENAME_URL_ADFS_TOKEN_EXPIRY} element={<TokenExpiryADFS />} />
                                <Route path={BASENAME_URL_AUTH_ADFS} element={<LogoutADFS />} />
                                <Route path="*" element={<Error404 />} />
                            </Routes>
                            <ToastContainer autoClose={2500} newestOnTop={true} />
                            <CookieConsent
                                ref={cookieConsentRef}
                                buttonStyle={{ display: 'none' }}
                                containerClasses={'cookie-banner'}
                                disableStyles={true}
                                buttonClasses="btn cookie-banner-button MuiButton-root MuiButton-contained"
                                buttonText={<IntlMessages id="label.cookies.acceptCookies" />}
                                overlayClasses="cookie"
                                overlay
                            >
                                <div className="d-flex">
                                    <CookieBanner />
                                    <div className="d-flex flex-column justify-content-center">
                                        <Button
                                            className="cookie-banner-button mb-3"
                                            onClick={cookiesHandler}
                                            color="primary"
                                            variant="contained"
                                        >
                                            <IntlMessages id="label.cookies.acceptCookies" />
                                        </Button>
                                        <Button
                                            className="tertiary-button mt-2"
                                            onClick={() => setIsCustomizeDialogOpen(true)}
                                            disableRipple
                                            disableFocusRipple
                                            variant="text"
                                        >
                                            <IntlMessages id="label.cookies.privacySettings" />
                                        </Button>
                                    </div>
                                </div>
                            </CookieConsent>
                            {isCustomizeDialogOpen && (
                                <CustomizeCookiesDialog
                                    isOpen={isCustomizeDialogOpen}
                                    setIsOpen={setIsCustomizeDialogOpen}
                                    cookieBannerHandler={cookieConsentRef.current}
                                />
                            )}
                        </div>
                    </IntlProvider>
                </LocalizationProvider>
                {/* </EfzThemeProvider> */}
                {/* </ThemeProvider> */}
            </StyledEngineProvider>
        </CacheBuster>
    );
};

export default App;
